import React, { ChangeEvent } from 'react';
import styles from './styles.module.css';
import { Title } from '../Title';
import { Switch } from '../Switch';
import { InputSearch } from '../InputSearch';
import { Dropdown, DropdownItem } from '../Dropdown';
import { DropdownTrigger } from '../Dropdown/DropdownTrigger';
import { DropdownTitle } from '../Dropdown/DropdownTitle';
import { DropdownContent } from '../Dropdown/DropdownContent';
import { IconType } from 'react-icons';
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  AccordionTrigger,
} from '../Accordion';

export type DropdownItems = {
  id: string;
  label: string;
  icon?: IconType;
  checked: boolean;
  setChecked: () => void;
};

export type DropdownData = {
  id: string;
  title: string;
  active: boolean;
  onToggle: () => void;
  dropdownItems: DropdownItems[];
};

export type Suggestions = {
  label: string;
  action: () => void;
};

export type SwitchData = {
  id: string;
  label: string;
  checked: boolean;
  setChecked: (checked: boolean) => void;
};

export type SideFilterProps = {
  title: string;
  icon?: React.ReactNode;
  insideAccordion?: boolean;
  handleToggle: (id: string) => void;
  active: boolean;
  searchPlaceholder: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchClick: () => void;
  onSearchClear?: () => void;
  switchData: SwitchData[];
  dropdownData: DropdownData[];
  searchValue?: string;
  suggestions?: Suggestions[];
};

export const SideFilter: React.FC<SideFilterProps> = ({
  title,
  searchPlaceholder,
  onSearchChange,
  onSearchClick,
  onSearchClear,
  switchData,
  dropdownData,
  icon,
  insideAccordion = false,
  handleToggle,
  searchValue,
  active,
  suggestions,
}) => {
  const renderContent = () => (
    <div className={styles.sideFilter}>
      {!insideAccordion && (
        <div className={styles.sideFilterTitle}>
          <Title title={title} as="h2" />
          {icon}
        </div>
      )}

      <section className={styles.searchContainer}>
        <label className={styles.label} htmlFor="search">
          Buscar por palavra-chave
        </label>

        <InputSearch
          id="search"
          style={{ width: '100%' }}
          placeholder={searchPlaceholder}
          onSubmit={onSearchClick}
          onChange={onSearchChange}
          onClear={onSearchClear}
          {...(searchValue && { value: searchValue })}
        />
        {suggestions && suggestions.length > 0 && (
          <ul className={styles.suggestionsPanel}>
            {suggestions.map(({ label, action }, index) => (
              <li
                key={index}
                role="option"
                aria-selected={false}
                tabIndex={0}
                onClick={action}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    action();
                  }
                }}
                className={styles.suggestionItem}
              >
                {highlightMatch(label, searchValue || '')}
              </li>
            ))}
          </ul>
        )}
      </section>

      <hr className={styles.divider} />

      {switchData.length > 0 && (
        <>
          <section className={styles.switchContainer}>
            <span className={styles.label}>Forma de realização</span>

            {switchData.map(({ id, label, setChecked, checked }) => (
              <span key={id} className={styles.switchField}>
                <Switch id={id} checked={checked} setChecked={setChecked} />

                <label htmlFor={id}>{label}</label>
              </span>
            ))}
          </section>

          <hr className={styles.divider} />
        </>
      )}

      <section>
        {dropdownData.map(({ id, title, active, onToggle, dropdownItems }) => (
          <Dropdown key={id}>
            <DropdownTrigger onToggle={onToggle} active={active}>
              <DropdownTitle>{title}</DropdownTitle>
            </DropdownTrigger>

            <DropdownContent active={active}>
              {dropdownItems.map(({ id, label, icon, checked, setChecked }) => (
                <DropdownItem
                  key={id}
                  id={id}
                  Icon={icon}
                  setChecked={setChecked}
                  checked={checked}
                >
                  {label}
                </DropdownItem>
              ))}
            </DropdownContent>
          </Dropdown>
        ))}
      </section>
    </div>
  );

  return insideAccordion ? (
    <Accordion background="transparent">
      <AccordionTrigger onToggle={handleToggle} active={active}>
        <AccordionTitle marker>{title}</AccordionTitle>
      </AccordionTrigger>
      <AccordionContent active={active}>{renderContent()}</AccordionContent>
    </Accordion>
  ) : (
    renderContent()
  );
};

const highlightMatch = (text: string, query: string) => {
  if (!query || !query.trim()) return text;

  const normalizedQuery = normalize(query).replace(
    /[\\^$.*+?()[\]{}|]/g,
    '\\$&'
  );
  const regex = new RegExp(normalizedQuery, 'gi');
  const normalizedText = normalize(text);

  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(normalizedText)) !== null) {
    const start = match.index;
    const end = start + match[0].length;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <strong key={start} className={styles.suggestionItemHighlight}>
        {text.slice(start, end)}
      </strong>
    );

    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const normalize = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
